import React, { useEffect, useMemo, useState } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FullScreen from "@mui/icons-material/Fullscreen";
import FullScreenExit from "@mui/icons-material/FullscreenExit";
import { Box, Button, CircularProgress, IconButton, Modal, Popover, Stack, Toolbar, Typography } from "@mui/material";
// import { TableCell } from "../reports/Measures-table/TableCell";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
// import telescopeTable from "./telescope-table-data.json";
import { useActionHook } from "hooks";
import { MaterialReactTable, type MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { useReportsActions } from "store";
import {
  // ClientDealTableRow,
  TelescopeTableRow
  // TelescopeTableColumn,
  // TelescopeTable as TelescopeTableType
} from "store/types";

interface ToolbarButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  renderIcon: React.JSX.Element;
  tooltip: string;
}

const ToolbarButton = ({ onClick, renderIcon, tooltip }: ToolbarButtonProps) => {
  return (
    <MaterialTooltip isShow title={tooltip} placement="top">
      <IconButton onClick={onClick}>{renderIcon}</IconButton>
    </MaterialTooltip>
  );
};

const stickyColumn = {
  position: "sticky",
  right: 0,
  background: "white",
  borderLeft: "1px solid #dae0e2"
};

export const TelescopeTable: React.FC = () => {
  const { sendBenchmarkEmailAction, getBenchmarkTelescopeTable } = useReportsActions();
  const [connectItem, setConnectItem] = useState<{ gpName: string; groupName: string } | null>(null);
  const [infoPopover, setInfoPopover] = useState<{ anchor: HTMLElement; text: string } | null>(null);

  const {
    action: getTable,
    loading,
    data,
    setData
  } = useActionHook(getBenchmarkTelescopeTable, {
    onSuccess: () => setConnectItem(null)
  });

  const { action: connect, loading: connectLoading } = useActionHook(sendBenchmarkEmailAction, {
    onSuccess: (table) => {
      setData(table);
      setConnectItem(null);
    }
  });

  useEffect(() => {
    getTable();
  }, []);

  const columnsTelescope = useMemo<MRT_ColumnDef<TelescopeTableRow>[]>(() => {
    const result: MRT_ColumnDef<TelescopeTableRow>[] = [];

    if (!data) return result;

    data.columns.forEach((item, index, arr) => {
      if (item.key === "gp_name") return;
      if (index !== arr.length - 1) {
        result.push({
          header: item.title,
          id: item.key,
          enableColumnDragging: false,
          enableColumnFilter: false,
          enableColumnOrdering: false,
          accessorKey: "valueData",
          accessorFn: (row: TelescopeTableRow) => row.valueData[item.key],
          sortingFn:
            item.key === "signal"
              ? (rowA, rowB, columnId) => {
                  const aValue = rowA["original"].valueData[columnId] as number;
                  const bValue = rowB["original"].valueData[columnId] as number;
                  if (aValue && bValue) {
                    return aValue - bValue;
                  }
                  return 0;
                }
              : "basic"
        });
      } else {
        result.push({
          header: "",
          id: item.key,
          accessorKey: item.key,
          enableColumnDragging: false,
          enableColumnFilter: false,
          enableSorting: false,
          enableColumnOrdering: false,
          // accessorFn: (row) => row.valueData[item.key],
          accessorFn: (row) => row.valueData[item.key],
          Cell: ({ row, column }) => {
            if (!row.original.valueData[column.id]) return;
            return (
              <Box sx={{ cursor: "pointer" }}>
                <Button size="sm" variant="outlined">
                  {row.original.valueData[column.id]}
                </Button>
              </Box>
            );
          }
        });
      }
    });

    result.unshift({
      header: "GP name",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableColumnOrdering: false,
      Cell: ({ cell }) => (
        <Stack direction="row" sx={{ justifyContent: "space-between", pl: "8px" }}>
          {cell.getValue<string>()}
          {/* <IconButton
            size="sm"
            sx={{ color: "#92A7BC" }}
            onClick={(e) => setInfoPopover({ anchor: e.currentTarget, text: cell.getValue<string>() })}
          >
            <InfoOutlinedIcon />
          </IconButton> */}
        </Stack>
      )
      // AggregatedCell: ({ cell, row }) => (
      //   <TableCell dealId={row.original.deal_id} value={cell.getValue<string>()} aggregated formatDate />
      // )
    });

    return result;
  }, [data]);

  const table = useMaterialReactTable<TelescopeTableRow>({
    columns: columnsTelescope,

    data: data?.rows || [],
    state: { isLoading: !data || loading },
    muiTablePaperProps: {
      sx: {
        boxShadow: 0,
        "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
          "&:first-child": {
            overflow: "visible !important"
          }
        }
      }
    },
    muiTableHeadCellProps: {
      sx: {
        // borderColor: "gray.50",
        height: "auto !important",
        " .MuiDivider-root": { borderWidth: 1 },
        ".Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap"
        },
        ":first-child": {
          maxWidth: "50px !important",
          minWidth: "50px !important",
          width: "50px"
        }
      },
      className: "classic-table__head-cell"
    },
    muiTableBodyCellProps: (props) => ({
      sx: {
        borderColor: "gray.50",
        align: "left",
        ":nth-of-type(n + 2)": {
          borderRight: "1px solid #dae0e2"
        }
      },
      onClick: () => {
        const gpName = props.row.original.valueData["gp_name"];
        const groupTitle = props.row.getParentRow()?.original.title;
        if (props.cell.id.includes("connect") && gpName && groupTitle) {
          setConnectItem({ gpName: gpName.toString(), groupName: groupTitle });
        }
      }
    }),
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "white",
        th: {
          bgcolor: "red",
          ":last-child": stickyColumn
        }
      },
      className: "classic-table__head-row"
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        // backgroundColor: "white",
        backgroundColor: "white",
        td: {
          ...(row.subRows?.length && { borderRight: "none !important" }),
          bgcolor: "white",
          ":first-child": {
            maxWidth: "50px !important",
            minWidth: "50px !important",
            width: "50px"
          },
          ":last-child": stickyColumn
        }
      }
    }),

    icons: {
      ArrowDownwardIcon: (props: { className: string }) => (
        <div className="measures-table__sort-icons">
          <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
          <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
        </div>
      ),
      DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
    },
    muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
    muiExpandAllButtonProps: { color: "primary" },
    muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } },

    renderTopToolbar: ({ table }) => {
      return (
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#173c4c",
            fontSize: "16px",
            height: "24px",
            minHeight: "24px !important",
            maxHeight: "24px",
            px: "0 !important",
            mb: "22px"
          }}
        >
          <Typography sx={{ fontSize: "16px", color: "#0A3142", fontWeight: 500 }}>Telescope</Typography>

          <ToolbarButton
            tooltip={"Toggle Expand"}
            onClick={() => table.setIsFullScreen(!table.getState().isFullScreen)}
            renderIcon={table.getState().isFullScreen ? <FullScreenExit /> : <FullScreen />}
          />
        </Toolbar>
      );
    },

    // getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,

    filterFromLeafRows: true,
    enableStickyHeader: true,
    enableExpanding: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableColumnDragging: true,
    enableColumnActions: false
  });

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "white",
        flexGrow: 1,
        p: "22px",
        boxShadow: "2px 2px 2px 0px rgba(0, 0, 0, 0.10)",
        borderRadius: "8px",
        overflow: "hidden",
        ".MuiTableContainer-root": {
          maxHeight: "calc(100% - 65px) !important",
          border: "1px solid #DBE8F5",
          borderRadius: "8px"
        },
        ".MuiPaper-root": {
          bgcolor: "transparent",
          height: "100%",
          boxShadow: "none",
          "> .MuiBox-root": {
            display: "none"
          }
        }
      }}
    >
      <MaterialReactTable table={table} />
      <Modal open={Boolean(connectItem)}>
        <Stack
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
            width: 520,
            height: 216,
            bgcolor: "white",
            p: "32px",
            gap: "20px",
            boxShadow:
              "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px"
          }}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", borderBottom: "1px solid #DBE8F5", pb: "6px" }}>
            <Typography sx={{ color: "#071F35", fontSize: "18px", lineHeight: "24px", fontWeight: 400 }}>
              Connect
            </Typography>
            <IconButton size="sm" onClick={() => setConnectItem(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography>We will contact the general partner and facilitate an introduction on your behalf</Typography>
          {connectLoading ? (
            <CircularProgress size={32} sx={{ alignSelf: "flex-end" }} />
          ) : (
            <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "8px" }}>
              <Button variant="outlined" onClick={() => setConnectItem(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!connectItem}
                onClick={() =>
                  connectItem && connect({ group_name: connectItem.groupName, partner_name: connectItem.gpName })
                }
              >
                Confirm
              </Button>
            </Stack>
          )}
        </Stack>
      </Modal>
      <Popover
        open={Boolean(infoPopover)}
        anchorEl={infoPopover?.anchor}
        onClose={() => setInfoPopover(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: "2px",
            boxShadow: "sm",
            fontSize: "12px",
            lineHeight: "16px",
            color: "#071F35",
            fontWeight: 400,
            p: "4px 8px"
          }}
        >
          {infoPopover?.text}
        </Box>
      </Popover>
    </Box>
  );
};
